import React, { FC, useEffect, useState } from 'react'
import { Validators as ValidatorsDS } from 'Modules'
import moment, { Moment } from 'moment'
import { UITypes, IconComponent, InputComponent, DatePickerComponent } from 'LibNeo'
import locale from 'antd/lib/date-picker/locale/ru_RU'
import { FieldNames } from 'Src/constants/FieldNames'
import { DATE_FORMAT, DATE_FORMAT_FOR_BACKEND, HELPER_FIELD_PREFIX } from 'Src/constants/Global'
import { validateDate } from 'Helpers'

import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import { CardMaskWrapper, DatePickerMaskWrapper } from './styled'
import { DatePickerWrapper } from '../styled'

export interface DatePickerMaskProps {
	form: WrappedFormUtils
	field: FieldNames
	label?: string
	tooltip?: string
	placeholder?: string
	initialValue?: string | Moment
	disabled?: boolean
	showToday?: boolean
	fieldOptions?: GetFieldDecoratorOptions
	disabledDate?: (current: moment.Moment | null) => boolean
	onChange?: (value?) => void
	requiredHighlight?: boolean
}

export const DatePickerMaskComponent: FC<DatePickerMaskProps> = ({
	form,
	field,
	label,
	tooltip,
	placeholder,
	initialValue,
	disabled = false, // гарантирует появление атрибута data-qa-disabled, ввиду типа boolean | undef
	fieldOptions,
	showToday = true,
	disabledDate,
	onChange,
	requiredHighlight,
}) => {
	const errors = form?.getFieldError(field)
	const helperField = `${HELPER_FIELD_PREFIX}${field}`
	const valueField = form?.getFieldValue(field)
	const valueHelperField = form?.getFieldValue(helperField)

	const [showCalendar, changeShowCalendar] = useState<boolean>(false)
	const transformMaskDateToDateBack = (value: string | Moment): string => {
		if (typeof value === 'string' && validateDate(value, DATE_FORMAT)) {
			return moment(value, DATE_FORMAT).format(DATE_FORMAT_FOR_BACKEND)
		}
		if (typeof value !== 'string') {
			return value.format(DATE_FORMAT_FOR_BACKEND)
		}
		return value
	}
	useEffect((): void => {
		form?.setFields({
			[helperField]: {
				value: valueHelperField || '',
				errors: errors?.[0] && [new Error(errors[0])],
			},
		})
	}, [JSON.stringify(errors)])

	useEffect((): void => {
		const prepareValueField = valueField?.match(/-/)
			? valueField.split('-').reverse().join('.')
			: valueField
		if (prepareValueField !== valueHelperField) {
			form?.setFields({
				[helperField]: {
					value: valueField?.split('-').reverse().join('.') || '',
					errors: errors?.[0] && [new Error(errors[0])],
				},
			})
		} else {
			form?.setFields({
				[helperField]: {
					value: valueHelperField || '',
					errors: errors?.[0] && [new Error(errors[0])],
				},
			})
		}
	}, [valueField])

	const onChangeInput = (value): void => {
		const prepareValue = transformMaskDateToDateBack(value)
		form?.setFields({ [field]: { value: prepareValue, touched: true } })
		form?.validateFields([field])
		onChange?.(prepareValue)
	}

	const onChangeCalendar = (value): void => {
		const { setFieldsValue } = form
		const prepareValue = moment(value).format(DATE_FORMAT)
		setFieldsValue({ [helperField]: prepareValue })
		onChangeInput(prepareValue)
		changeShowCalendar(false)
		if (onChange) {
			onChange(value)
		}
	}

	const value = moment(form?.getFieldValue(helperField), DATE_FORMAT)
	const valueForPicker = value.isValid() ? value : moment()
	const customRules = fieldOptions ? fieldOptions.rules : []

	return (
		<>
			<DatePickerMaskWrapper>
				<InputComponent
					form={form}
					fieldName={field}
					placeholder=""
					type="hidden"
					fieldOptions={{
						rules: [
							{
								validator: ValidatorsDS.checkMomentDate(),
							},
							...customRules,
						],
						initialValue: initialValue && moment(initialValue).format(DATE_FORMAT_FOR_BACKEND),
					}}
				/>
			</DatePickerMaskWrapper>
			<InputComponent
				label={label}
				tooltip={tooltip}
				placeholder={placeholder}
				form={form}
				fieldName={helperField}
				onChange={onChangeInput}
				mask="_"
				format="##.##.####"
				disabled={disabled}
				requiredHighlight={requiredHighlight}
				suffix={
					<IconComponent
						name="calendar"
						type={UITypes.PASSIVE}
						onClick={(): void => changeShowCalendar(!showCalendar)}
					/>
				}
			/>
			<CardMaskWrapper id="calendar">
				<DatePickerWrapper>
					<DatePickerComponent
						open={showCalendar}
						format={DATE_FORMAT}
						locale={locale}
						showToday={showToday}
						value={valueForPicker}
						disabled={disabled}
						disabledDate={disabledDate}
						onChange={onChangeCalendar}
						onOpenChange={(open): void => changeShowCalendar(open)}
					/>
				</DatePickerWrapper>
			</CardMaskWrapper>
		</>
	)
}
